/**
 * Corrección tolerante de búsquedas (acentos ya se pliegan en normalize()).
 * Corrige errores comunes de escritura ("holbx", "chichen itsa", "cosumel")
 * contra un vocabulario de destinos conocidos, en español e inglés.
 */
import { CITY_CATALOG, normalize } from "./tour-search";

/** Sinónimos inglés → término presente en los textos del catálogo. */
const SYNONYMS: Record<string, string> = {
  island: "isla",
  women: "mujeres",
  "mexico city": "cdmx",
  ruins: "ruinas",
  yacht: "yate",
  boat: "barco",
  whale: "ballena",
  turtles: "tortugas",
  sharks: "tiburon",
};

const STOP = new Set(["tour", "tours", "en", "de", "del", "la", "el", "in", "to", "the", "a"]);

function vocabulary(): string[] {
  const set = new Set<string>();
  for (const [slug, c] of Object.entries(CITY_CATALOG)) {
    for (const w of [slug, c.name, ...c.terms].flatMap((s) => normalize(s).split(/[\s-]+/))) {
      if (w.length >= 4) set.add(w);
    }
  }
  ["cenote", "cenotes", "snorkel", "yate", "ballena", "tiburon", "flamingos", "laguna", "ruinas", "akumal", "coloradas", "lagartos", "xcaret"].forEach((w) => set.add(w));
  return [...set];
}

let VOCAB: string[] | null = null;

export function editDistance(a: string, b: string): number {
  if (a === b) return 0;
  const dp = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    let prev = dp[0];
    dp[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const tmp = dp[j];
      dp[j] = Math.min(dp[j] + 1, dp[j - 1] + 1, prev + (a[i - 1] === b[j - 1] ? 0 : 1));
      prev = tmp;
    }
  }
  return dp[b.length];
}

function fixToken(tok: string): string {
  VOCAB ??= vocabulary();
  if (tok.length < 4 || VOCAB.includes(tok)) return tok;
  const max = tok.length >= 7 ? 2 : 1;
  let best = tok;
  let bestD = max + 1;
  for (const w of VOCAB) {
    if (Math.abs(w.length - tok.length) > max) continue;
    // Prefijo: "chich" → "chichen"
    if (w.startsWith(tok)) return tok;
    const d = editDistance(tok, w);
    if (d < bestD) {
      bestD = d;
      best = w;
    }
  }
  return bestD <= max ? best : tok;
}

/** Devuelve la consulta corregida (normalizada) o la misma si no hay cambio. */
export function correctQuery(query: string): string {
  let q = normalize(query);
  for (const [en, es] of Object.entries(SYNONYMS)) {
    q = q.replace(new RegExp(`\\b${en}\\b`, "g"), es);
  }
  return q
    .split(/\s+/)
    .filter((t) => t && !STOP.has(t))
    .map(fixToken)
    .join(" ");
}

export type DestinationHit = { slug: string; name: string; region: string };

/** Destinos del catálogo que coinciden (tolerante) con la consulta. */
export function matchDestinations(query: string, limit = 4): DestinationHit[] {
  const q = correctQuery(query);
  if (!q) return [];
  const toks = q.split(" ");
  return Object.entries(CITY_CATALOG)
    .filter(([slug, c]) => {
      const hay = normalize(`${slug} ${c.name} ${c.region} ${c.terms.join(" ")}`);
      return toks.every((t) => hay.includes(t));
    })
    .slice(0, limit)
    .map(([slug, c]) => ({ slug, name: c.name, region: c.region }));
}

/** Destinos "parecidos" para cuando no hay resultados (letra inicial / distancia). */
export function similarDestinations(query: string, limit = 4): DestinationHit[] {
  const q = normalize(query).replace(/\s+/g, "");
  if (!q) return [];
  return Object.entries(CITY_CATALOG)
    .map(([slug, c]) => ({ slug, c, d: editDistance(q.slice(0, 8), normalize(c.name).replace(/\s+/g, "").slice(0, 8)) }))
    .sort((a, b) => a.d - b.d)
    .slice(0, limit)
    .map(({ slug, c }) => ({ slug, name: c.name, region: c.region }));
}
